import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { primePhotoViewer } from '../utils/photoViewerCache'
import Gap from './Gap'
import SheetHeader from './SheetHeader'
import { ReviewProps } from '../types/review'
import Avatar from './Avatar'
import { seedReviewDetailCache } from '../utils/homeFeedCache'
import { useCategoryStore } from '../stores/categoryIconStore'
import { useCommentCountStore } from '../stores/commentCountStore'
import ReviewLikeCount from './ReviewLikeCount'
import ReviewCommentCount from './ReviewCommentCount'
import PlaceAddButton from './PlaceAddButton'
import BookmarkButton from './BookmarkButton'
import { getVisitTypeDisplayLabel } from '../constants/visitType'
import { getShortAddress } from '../utils/address'
import { useAuthStore } from '../stores/authStore'
import { formatScoreOneDecimal } from '../utils/scoreFormat'
import ListPhoto from './ListPhoto'
import PrivateBadge from './PrivateBadge'
import VisitThread from './VisitThread'

interface FeedReviewBoxProps {
  review: ReviewProps
  disableReviewDetailClick?: boolean
  /**
   * 댓글 버튼을 눌렀을 때. 자세히보기처럼 같은 화면에 입력칸이 있으면
   * 그리로 커서를 보낸다. 안 넘기면 자세히보기로 이동한다.
   */
  onCommentClick?: () => void
  hideScore?: boolean
}

const FeedReviewBox: React.FC<FeedReviewBoxProps> = ({
  review,
  disableReviewDetailClick = false,
  onCommentClick,
  hideScore = false,
}) => {
  const navigate = useNavigate()
  const { userId: currentUserId } = useAuthStore()
  // 댓글 수: 리뷰 상세에서 실제로 센 값이 있으면 그게 최신이다.
  // 목록 값(review.comments)은 홈 스냅샷에 담겨 되살아나므로 댓글을 달고
  // 돌아오면 예전 숫자로 돌아가 있었다. commentCountStore 주석 참고.
  const commentCount = useCommentCountStore(
    (s) => s.counts.get(review.id) ?? review.comments
  )
  const [showTaggedSheet, setShowTaggedSheet] = useState(false)
  const [sheetTranslateY, setSheetTranslateY] = useState(0)
  const sheetDragStartY = useRef(0)
  const isSheetDragging = useRef(false)

  // 리뷰 텍스트 더보기/접기 상태
  const [isReviewExpanded, setIsReviewExpanded] = useState(false)
  /*
   * 두 줄에 맞춰 잘라낸 텍스트. 세 가지 상태를 구분한다.
   *   undefined — 아직 재보지 않았다
   *   null      — 재봤고, 두 줄 안에 들어가서 자를 필요가 없다
   *   문자열    — 재봤고, 이만큼만 보여주면 된다
   *
   * 예전에는 undefined 자리가 없어서 '안 재봤다'와 '자를 필요 없다'가
   * 둘 다 null 이었다. 그래서 첫 그림에 전체 글이 세 줄로 나왔다가 다음
   * 프레임에 두 줄로 접히면서, 그 아래 카드가 전부 한 줄만큼 위로 튀었다.
   * 안 재본 동안에는 CSS 로 두 줄만 보이게 해서 높이를 미리 맞춰 둔다.
   */
  const [truncatedReviewText, setTruncatedReviewText] = useState<
    string | null | undefined
  >(undefined)
  const reviewTextRef = useRef<HTMLDivElement>(null)
  const reviewMeasureRef = useRef<HTMLDivElement>(null)

  // 텍스트를 두 줄에 맞춰 자르는 측정 함수 (이진 탐색)
  const measureReviewTruncation = useCallback(() => {
    if (disableReviewDetailClick || isReviewExpanded) return
    const fullText = review.reviewText
    const measure = reviewMeasureRef.current
    if (!fullText || !measure) {
      setTruncatedReviewText(null)
      return
    }

    const lineHeight = parseFloat(
      getComputedStyle(measure).lineHeight || '0'
    )
    if (!lineHeight) {
      // 줄 높이를 못 읽으면 잴 수가 없다. 접힌 채로 두면 글이 영영 가려지니
      // 예전처럼 전체 글을 보여준다.
      setTruncatedReviewText(null)
      return
    }
    const maxHeight = lineHeight * 2 + 1 // 1px 오차 허용

    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    const toHtml = (s: string) => escapeHtml(s).replace(/\n/g, '<br/>')

    // 1) 전체 텍스트가 두 줄에 들어가면 자를 필요 없음
    measure.innerHTML = toHtml(fullText)
    if (measure.scrollHeight <= maxHeight) {
      setTruncatedReviewText(null)
      return
    }

    // 2) "...더보기"가 붙은 상태에서 두 줄에 들어가는 최대 길이를 이진 탐색
    const suffixHtml = '... <span class="text-textColorText3">더보기</span>'
    let lo = 0
    let hi = fullText.length
    let best = 0
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2)
      const sliced = fullText.slice(0, mid).replace(/\s+$/, '')
      measure.innerHTML = toHtml(sliced) + suffixHtml
      if (measure.scrollHeight <= maxHeight) {
        best = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }
    setTruncatedReviewText(fullText.slice(0, best).replace(/\s+$/, ''))
  }, [review.reviewText, disableReviewDetailClick, isReviewExpanded])

  /*
   * 텍스트 / 펼침상태 변경 시 측정.
   *
   * useEffect 가 아니라 useLayoutEffect 인 이유: useEffect 는 브라우저가
   * 화면을 다 그린 뒤에 돌아간다. 그러면 '전체 글' 한 프레임이 반드시
   * 눈에 보인다. useLayoutEffect 는 그리기 직전에 돌아가므로 잘린 글이
   * 첫 프레임부터 나온다.
   */
  useLayoutEffect(() => {
    measureReviewTruncation()
  }, [measureReviewTruncation])

  // 컨테이너 크기 변경 시 재측정 (반응형 대응)
  useEffect(() => {
    if (disableReviewDetailClick || isReviewExpanded) return
    const container = reviewTextRef.current
    if (!container) return
    const observer = new ResizeObserver(() => measureReviewTruncation())
    observer.observe(container)
    return () => observer.disconnect()
  }, [disableReviewDetailClick, isReviewExpanded, measureReviewTruncation])

  // 바텀시트 열림 시 body 스크롤 잠금
  useEffect(() => {
    if (showTaggedSheet) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [showTaggedSheet])

  const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    sheetDragStartY.current = e.touches[0].clientY
    isSheetDragging.current = true
    setSheetTranslateY(0)
  }, [])

  const handleSheetTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    if (!isSheetDragging.current) return
    const diff = e.touches[0].clientY - sheetDragStartY.current
    if (diff > 0) {
      setSheetTranslateY(diff)
    }
  }, [])

  const handleSheetTouchEnd = useCallback(() => {
    if (!isSheetDragging.current) return
    isSheetDragging.current = false
    if (sheetTranslateY > 80) {
      setShowTaggedSheet(false)
    }
    setSheetTranslateY(0)
  }, [sheetTranslateY])
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown
  )

  const handleUserRowClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!review.userId) return
    e.stopPropagation()
    if (review.userId !== currentUserId) {
      navigate(`/user/${review.userId}/profile`)
      return
    }
    navigate('/main-my')
  }

  const displayUserLogin =
    review.user.username?.trim() || review.userId || ''

  // 접지 않는 자리(자세히보기)이거나, 사용자가 이미 펼쳤다
  const neverCollapse = disableReviewDetailClick || isReviewExpanded
  // 아직 재보지 않았다 → 이 한 프레임 동안은 CSS 로 두 줄만 보여 준다
  const clampUntilMeasured = !neverCollapse && truncatedReviewText === undefined
  // 전체 글을 그대로 그릴 때. 안 재봤으면 위의 clamp 가 두 줄로 가려 준다
  const showFullReviewText =
    neverCollapse ||
    truncatedReviewText === null ||
    truncatedReviewText === undefined

  return (
    <div
      className={`w-full border-b border-472410-10% px-20px ${disableReviewDetailClick ? '' : 'cursor-pointer'}`}
      onClick={
        disableReviewDetailClick
          ? undefined
          : () => {
              // 지금 그려져 있는 내용을 그대로 넘겨 자세히보기가 빈 화면으로
              // 시작하지 않게 한다
              seedReviewDetailCache(review)
              navigate(`/review-detail/${review.id}`)
            }
      }
    >
      <Gap height={16} />

      {/* 사용자 정보 */}
      <div
        className={`w-full flex items-center ${review.userId ? 'cursor-pointer' : ''}`}
        onClick={review.userId ? handleUserRowClick : undefined}
      >
        <Avatar
          nickname={displayUserLogin || review.user.nickname}
          profileImage={review.user.profileImage}
          size={48}
        />

        <Gap width={8} />

        <span
          className={`flex-1 font-pretendard font-bold text-14px text-brown-80%-main text-ellipsis-1`}
        >
          {displayUserLogin}
        </span>
      </div>

      <Gap height={16} />

      {/* 가게 정보 */}
      <div
        className="flex items-center cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          navigate(`/main-map?placeId=${review.placeId}`)
        }}
      >
        {getCategoryIconBrown(review.restaurant.category) && (
          <img
            src={getCategoryIconBrown(review.restaurant.category)!}
            alt="가게"
            className={`w-24px h-28px object-contain ${
              review.restaurant.category === 'cafe'
                ? '-ml-[1.4px]'
                : '-ml-[2px]'
            }`}
          />
        )}

        <Gap width={8} />

        <span className="flex-1 font-pretendard font-bold text-16px text-brown-80%-main text-ellipsis-1">
          {review.restaurant.name}
        </span>

        <Gap width={8} />

        <span className="font-pretendard font-bold text-16px text-brown-80%-main text-right">
          {hideScore ? '? 점' : `${formatScoreOneDecimal(review.restaurant.rating)} 점`}
        </span>
      </div>

      {/* 이미지 섹션 */}
      {review.images && review.images.length > 0 && (
        <>
          <Gap height={16} />

          <div
            className={`
              w-full
              overflow-x-auto
              overflow-y-hidden
              scrollbar-hide
              [overscroll-behavior-x:contain]
              [-webkit-overflow-scrolling:touch]
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`
                flex
                flex-nowrap
                gap-8px
                w-max
                min-w-full
              `}
            >
              {review.images.map((image, index) => (
                <div
                  key={index}
                  className="shrink-0 w-128px h-160px rounded-16px overflow-hidden bg-brandPrimary-5% cursor-pointer"
                  onClick={(e) => {
                    // 카드 전체가 상세보기로 가는 클릭을 갖고 있다.
                    // 막지 않으면 사진을 눌러도 상세보기로 밀려난다.
                    e.stopPropagation()
                    // 피드가 이미 아는 내용을 뷰어 자리에 미리 놓는다.
                    // 안 그러면 뷰어가 서버를 두 번 기다리는 0.3초 동안
                    // 화면 전체가 크림색이라 흰 화면이 번쩍인다.
                    primePhotoViewer(review)
                    navigate(`/review-photo/${review.id}/${index}`)
                  }}
                >
                  <ListPhoto
                    src={image}
                    alt={`기록 이미지 ${index + 1}`}
                    className="w-full h-full object-cover pointer-events-none"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 리뷰 텍스트 */}
      {review.reviewText && (
        <>
          <Gap height={16} />

          <div className="relative">
            {/* 실제 표시되는 텍스트 */}
            <div
              ref={reviewTextRef}
              className={`text-style-body2 text-textColorText2 whitespace-pre-wrap break-words ${
                clampUntilMeasured ? 'text-ellipsis-2' : ''
              }`}
            >
              {showFullReviewText ? (
                review.reviewText
              ) : (
                <>
                  {truncatedReviewText}
                  {'... '}
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsReviewExpanded(true)
                    }}
                    className="text-textColorText3 cursor-pointer"
                  >
                    더보기
                  </span>
                </>
              )}
            </div>
            {/* 측정용 보이지 않는 동일 스타일 박스 */}
            {!neverCollapse && (
              <div
                ref={reviewMeasureRef}
                aria-hidden="true"
                /*
                 * contain: layout — 이 박스 안의 배치가 바깥과 끊긴다.
                 * 자를 길이를 찾느라 글자를 열 번쯤 갈아끼우는데, 이게
                 * 없으면 한 번 갈아끼울 때마다 문서 전체 배치가 다시
                 * 계산된다. 목록에 카드가 수십 개면 그 값이 커진다.
                 */
                style={{ contain: 'layout' }}
                className="text-style-body2 whitespace-pre-wrap break-words absolute left-0 right-0 top-0 invisible pointer-events-none"
              />
            )}
          </div>
        </>
      )}

      {/* 태그된 친구 */}
      {review.taggedFriends && review.taggedFriends.length > 0 && (
        <>
          {/* 텍스트 있으면 텍스트↔태그 8px, 없으면 사진↔태그가 16px로 일관되게 */}
          <Gap height={review.reviewText ? 8 : 16} />
          <div
            className="flex items-center text-style-body2 overflow-hidden"
            style={{ maxWidth: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="truncate">
              {review.taggedFriends.map((friend, index) => (
                <React.Fragment key={friend.id}>
                  {index > 0 && ', '}
                  <span
                    className="font-bold text-textColorText2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/user/${friend.id}/profile`)
                    }}
                  >
                    {friend.nickname}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <span
              className="text-textColorText3 shrink-0 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                setShowTaggedSheet(true)
              }}
            >
              님과 함께
            </span>
          </div>
        </>
      )}

      {/* 태그된 친구 바텀시트 */}
      {showTaggedSheet && review.taggedFriends && review.taggedFriends.length > 0 && (
        <>
          {/* 오버레이 - 터치 시 바텀시트만 닫고 아래 요소 이벤트 차단 */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-[1050]"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowTaggedSheet(false)
            }}
            onTouchEnd={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowTaggedSheet(false)
            }}
          />
          {/* 바텀시트 */}
          <div
            className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center pointer-events-none"
          >
            <div className="app-container flex flex-col pointer-events-auto">
              <div
                className="bg-ivoryKey rounded-t-30px max-h-[60vh] flex flex-col overflow-hidden w-full"
                onClick={(e) => e.stopPropagation()}
                style={{
                  transform: `translateY(${sheetTranslateY}px)`,
                  transition: isSheetDragging.current ? 'none' : 'transform 0.2s ease',
                }}
              >
                {/* 그랩 핸들 - 드래그 영역 */}
                <div
                  className="flex flex-col w-full cursor-grab"
                  onTouchStart={handleSheetTouchStart}
                  onTouchMove={handleSheetTouchMove}
                  onTouchEnd={handleSheetTouchEnd}
                >
                  <Gap height={24} />
                  <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto" />
                  <Gap height={12} />
                </div>
                {/* 헤더 (공용) */}
                <SheetHeader title="태그된 친구" />
                {/*
                  유저 목록.
                  이 시트만 하단 여백이 없어 마지막 사람이 시트 끝에 그대로 붙었다.
                  항목 자체가 py-12px이라 이미 12px이 깔려 있으므로 12px만 더해 총 24px.
                  스크롤 영역 안쪽에 줘야 목록이 짧을 때와 끝까지 스크롤했을 때 모두
                  여백이 남는다.
                */}
                <div className="flex-1 overflow-y-auto pb-12px">
                  {review.taggedFriends.map((friend) => (
                    <div
                      key={friend.id}
                      className="flex items-center gap-12px px-20px py-12px cursor-pointer"
                      onClick={() => {
                        setShowTaggedSheet(false)
                        navigate(`/user/${friend.id}/profile`)
                      }}
                    >
                      <Avatar
                        nickname={friend.nickname}
                        profileImage={friend.profileImage || null}
                        size={48}
                      />
                      <div className="flex flex-col">
                        <span className="font-pretendard font-bold text-14px text-textColorText2">
                          {friend.username || friend.nickname}
                        </span>
                        <span className="font-pretendard font-regular text-14px text-textColorText2">
                          {friend.nickname}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 상호작용 섹션 */}
      <Gap height={16} />
      <div
        className="w-full flex items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 flex items-center gap-8px">
          {/* 좋아요 */}
          <ReviewLikeCount
            reviewId={review.id}
            initialLikeCount={review.likes}
            initialIsLiked={review.isLiked}
          />

          {/* 댓글 — 상호작용 영역 stopPropagation으로 카드 클릭이 막히므로 별도 이동 */}
          <ReviewCommentCount
            commentCount={commentCount}
            onClick={
              onCommentClick ??
              (() => {
                // 피드에서는 자세히보기로 옮겨가면서 '댓글 쓰러 왔다'고 알린다
                seedReviewDetailCache(review)
                navigate(`/review-detail/${review.id}`, {
                  state: { focusComment: true },
                })
              })
            }
          />
        </div>

        <div className="flex items-center gap-12px">
          {/* 리뷰 작성 */}
          <PlaceAddButton
            storeId={review.placeId}
            buttonSize={26}
            isAdded={review.hasUserReview || false}
          />

          {/* 북마크 */}
          <BookmarkButton
            storeId={review.placeId.toString()}
            buttonSize={26}
            isBookmarked={review.isBookmarked || false}
            placeName={review.restaurant.name}
            placeAddress={review.placeAddress}
          />
        </div>
      </div>

      {/* 푸터 섹션 */}
      <Gap height={16} />
      <div className="w-full flex items-center py-4px">
        <span className="flex-1 text-style-captionM text-textColorText3 text-ellipsis-1 text-left flex items-center">
          {/* 나만 보기 기록이면 날짜 앞에 표시한다. isPublic이 없는 옛 캐시는 공개로 본다 */}
          {review.isPublic === false && (
            <>
              <PrivateBadge />
              <span className="mx-5px">·</span>
            </>
          )}
          {/* 회차는 이전 기록이 있을 때만 붙인다. 한 번만 간 곳에 '1번째 기록'은 군더더기다 */}
          {review.earlierVisits && review.earlierVisits.length > 0 && (
            <>
              {review.visitNumber}번째 기록
              <span className="mx-5px">·</span>
            </>
          )}
          {/* 서버가 '2026년 9월 10일'로 만들어 보내준다. 옛 캐시에는 없어서 점 형식으로 물러난다 */}
          {review.dateDisplay || review.date}
        </span>

        {/*
          오른쪽 끝은 **동네**다. 예전엔 '매장 이용 / 배달 및 포장'이 있었는데,
          피드를 훑을 때 알고 싶은 건 '어디쯤인가'지 이용 방식이 아니다.
          이용 방식은 기록을 열면 영수증 카드에 그대로 있다.

          주소는 시도+시군구까지만 자른다(getShortAddress). 도로명까지 두면
          한 줄에 안 들어가고, 가게 이름 옆에서 길어지면 날짜를 밀어낸다.

          주소가 없는 기록은 옛 이용 방식으로 물러난다 — 장소가 지워졌거나
          캐시에 placeAddress 가 없던 시절 데이터가 있다.
        */}
        <span className="text-style-captionM text-textColorText3 text-ellipsis-1 text-right">
          {review.placeAddress
            ? getShortAddress(review.placeAddress)
            : getVisitTypeDisplayLabel(review.visitType)}
        </span>
      </div>

      {/* 같은 장소의 이전 회차 — 최신이 본문, 아래로 갈수록 예전 기록 */}
      {review.earlierVisits && review.earlierVisits.length > 0 && (
        <VisitThread
          visits={review.earlierVisits}
          hideScore={review.hideScore}
        />
      )}

      <Gap height={8} />

    </div>
  )
}

export default FeedReviewBox
